<template>
  <!--
    Full-bleed rail breakout.

    Purely presentational. Renders its slot (a horizontally-scrolling rail) and
    breaks it out of the centered, max-width-constrained page content so the
    carousel visually overflows to the page COLUMN'S region edges, while the
    section header and the rest of the page body stay centered.

    The breakout width comes from the `--channel-page-region-width` CSS variable
    published by `channel-layout-v2.vue` on the page-column wrapper
    (`.channel-page-region`). That variable equals:
      - chat closed / mobile : the full viewport width
      - chat open on desktop : viewport - chat column width
    so the same region-relative formula bleeds to the viewport edges when chat
    is closed AND to the page-region edges (never under the chat column) when
    chat is open — without ever assuming `100vw`.

    Left-aligned breakout: the wrapper widens to the region width and its left
    edge stays flush with the constrained content column (margin-left: 0). Only
    the right margin is negated by the full difference between the region width
    and the element's own constrained width (`100%`), so the right edge bleeds
    out to the page-region boundary while the left edge stays in place.

    Containment: the breakout is clipped by `overflow-x: clip` declared on
    `.channel-page-region` (the same element that hosts the variable), so it
    never produces a horizontal document scrollbar. This component does NOT
    apply overflow on `body` or any ancestor above the page-column wrapper.

    The rail's own internal left padding (`px-5` inside ChannelRailVirtualized)
    aligns the first card with the constrained content edge; the rail then
    bleeds to the right/left gutter past it, matching the Figma carousel start.

    When `enabled` is false (legacy layout path) it renders its slot with NO
    wrapping element, so the legacy rail DOM is byte-for-byte unchanged.
  -->
  <div v-if="enabled" class="full-bleed-rail">
    <slot></slot>
  </div>
  <slot v-else></slot>
</template>

<script setup lang="ts">
/**
 * Presentational only — no internal state, no signal subscription in script.
 * It consumes the `--channel-page-region-width` CSS variable from the
 * page-column wrapper (`channel-layout-v2.vue`) purely through CSS, so it works identically
 * regardless of chat-open / chat-closed state and stays trivially testable.
 *
 * `enabled` gates the breakout so the same component is used on both layout
 * paths: callers pass the `enable_new_channel_layout` flag and the legacy path
 * (false) gets a transparent fragment passthrough.
 */
withDefaults(defineProps<{ enabled?: boolean }>(), { enabled: true });
</script>

<style scoped>
.full-bleed-rail {
  /*
   * Region-relative full-bleed. `--channel-page-region-width` is the page
   * column's effective horizontal region (full viewport when chat is closed,
   * viewport minus the chat column when chat is open). Falls back to 100vw so
   * the breakout still reaches the viewport edges before the variable is
   * measured or when rendered outside the v2 shell.
   */
  --fbr-region-width: var(--channel-page-region-width, 100vw);

  width: var(--fbr-region-width);
  /*
   * Asymmetric breakout: left edge stays flush with the constrained content
   * column (margin-left: 0), right edge bleeds out to the page-region edge.
   * The negative right margin pushes the right side of the wrapper past the
   * content box by the full difference between the region width and 100%.
   * `.channel-page-region` clips the overhang via overflow-x: clip, so no
   * horizontal scrollbar is introduced.
   */
  margin-left: 0;
  margin-right: calc((var(--fbr-region-width) - 100%) * -1);
}
</style>
